import { InPlay } from '@ps/isp-web-component-library/icons/InPlay';
import type { TranslationTag } from '@ps/web-core-intl';
import { Translation } from '@ps/web-shared-components';
import type {
  BasketballFixture,
  BasketballPeriod,
  BasketballSegment
} from '@ps/web-shared-generated-graphql/sports';

import EventStatusMessage from '../../EventStatusMessage/EventStatusMessage';
import FormatEventStatusDuration from '../FormatEventStatusDuration/FormatEventStatusDuration';
import GameTime from '../GameTime/GameTime';

import {
  TXTWCL_End_of_H1,
  TXTWCL_End_of_H2,
  TXTWCL_End_of_Overtime,
  TXTWCL_End_of_Q1,
  TXTWCL_End_of_Q2,
  TXTWCL_End_of_Q3,
  TXTWCL_End_of_Q4,
  TXTWCL_Game_Ended,
  TXTWCL_H1,
  TXTWCL_H2,
  TXTWCL_In_Play,
  TXTWCL_Overtime,
  TXTWCL_Q1,
  TXTWCL_Q2,
  TXTWCL_Q3,
  TXTWCL_Q4
} from './BasketballScoreboardInPlayStatus.messages.json';

const getTranslationTag = (
  period: BasketballPeriod,
  segment?: BasketballSegment | null
): TranslationTag => {
  switch (period) {
    case 'PERIOD_1':
      return segment === 'H1' ? TXTWCL_H1 : TXTWCL_Q1;
    case 'PERIOD_2':
      return segment === 'H2' ? TXTWCL_H2 : TXTWCL_Q2;
    case 'PERIOD_3':
      return TXTWCL_Q3;
    case 'PERIOD_4':
      return TXTWCL_Q4;
    case 'END_PERIOD_1':
      return segment === 'H1' ? TXTWCL_End_of_H1 : TXTWCL_End_of_Q1;
    case 'END_PERIOD_2':
      return segment === 'H2' ? TXTWCL_End_of_H2 : TXTWCL_End_of_Q2;
    case 'END_PERIOD_3':
      return TXTWCL_End_of_Q3;
    case 'END_PERIOD_4':
      return TXTWCL_End_of_Q4;
    case 'OVERTIME':
      return TXTWCL_Overtime;
    case 'END_OVERTIME':
      return TXTWCL_End_of_Overtime;
    case 'END':
      return TXTWCL_Game_Ended;
    case 'UNKNOWN_PERIOD':
      return TXTWCL_In_Play;
    default:
      return TXTWCL_In_Play;
  }
};

interface Props {
  basketballFixture: BasketballFixture;
}

const BasketballScoreboardInPlayStatus: React.FC<Props> = ({ basketballFixture }) => {
  const { score, clock } = basketballFixture;

  if (!score || !clock) {
    return <EventStatusMessage messageId="IN_PLAY" icon={InPlay} size="XSmall" />;
  }

  switch (clock.period) {
    case 'END':
    case 'END_PERIOD_1':
    case 'END_PERIOD_2':
    case 'END_PERIOD_3':
    case 'END_PERIOD_4':
    case 'END_OVERTIME':
    case 'UNKNOWN_PERIOD':
      return (
        <GameTime>
          <span>
            <Translation {...getTranslationTag(clock.period, clock.segment)} />
          </span>
        </GameTime>
      );
    case 'PERIOD_1':
    case 'PERIOD_2':
    case 'PERIOD_3':
    case 'PERIOD_4':
    case 'OVERTIME':
      return (
        <GameTime>
          <span>
            <Translation {...getTranslationTag(clock.period, clock.segment)} />
            <FormatEventStatusDuration intervalInSeconds={clock.timeRemaining} isCountdownTimer />
          </span>
        </GameTime>
      );
    default:
      return <EventStatusMessage messageId="IN_PLAY" icon={InPlay} size="XSmall" />;
  }
};

export default BasketballScoreboardInPlayStatus;
